import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Spinner, Alert, Avatar } from '@/components/ui';
import { Steps, type Step } from '@/components/ui/Steps/Steps';
import { LogOut, ChevronLeft, ChevronRight, SkipForward, Check, Mail } from 'lucide-react';
import { useAuthStore } from '@/stores/authStore';
import { useOrgStore } from '@/stores/orgStore';
import { useLocaleStore } from '@/stores/localeStore';
import { useRorosStore } from '@/stores/rorosStore';
import { useNotificationStore } from '@/stores/notificationStore';
import { setLocale, t } from '@/lib/i18n';
import { apiFetch } from '@/lib/api';
import { applyTheme, type ResolvedTheme } from '@/lib/orgTheme';
import {
  useRorosLaunchpadStatus,
  useRorosLaunchpadSubmitStep,
  useRorosLaunchpadComplete,
} from '@/hooks/roros/launchpad';
import {
  LAUNCHPAD_STEPS,
  STEP_LABELS,
  STEP_DEFAULTS,
  normalizeActiveDomains,
  type LaunchpadStepKey,
  type ActiveDomainEntry,
} from './launchpad/types';
import type { LocaleSettings } from '@/lib/localeFormatters';
import { RorosOperatingMode } from '@/types/roros/enums';
import { StepOperatingMode } from './launchpad/StepOperatingMode';
import { StepActiveDomains } from './launchpad/StepActiveDomains';
import { StepAIProvider } from './launchpad/StepAIProvider';
import { StepLocaleTz } from './launchpad/StepLocaleTz';
import { StepCadence } from './launchpad/StepCadence';
import { StepSummary } from './launchpad/StepSummary';

// ─── Country → Locale/Timezone/Currency mapping ──────────────────────────────
// Derives sensible Step 4 defaults from the org's registration data.

const COUNTRY_DEFAULTS: Record<string, { locale: string; timezone: string; currency: string }> = {
  'Germany': { locale: 'de-DE', timezone: 'Europe/Berlin', currency: 'EUR' },
  'Austria': { locale: 'de-DE', timezone: 'Europe/Vienna', currency: 'EUR' },
  'Switzerland': { locale: 'de-DE', timezone: 'Europe/Zurich', currency: 'CHF' },
  'United States': { locale: 'en-US', timezone: 'America/New_York', currency: 'USD' },
  'United Kingdom': { locale: 'en-GB', timezone: 'Europe/London', currency: 'GBP' },
  'France': { locale: 'de-DE', timezone: 'Europe/Paris', currency: 'EUR' },
  'Netherlands': { locale: 'de-DE', timezone: 'Europe/Amsterdam', currency: 'EUR' },
  'Belgium': { locale: 'de-DE', timezone: 'Europe/Brussels', currency: 'EUR' },
  'Italy': { locale: 'de-DE', timezone: 'Europe/Rome', currency: 'EUR' },
  'Spain': { locale: 'de-DE', timezone: 'Europe/Madrid', currency: 'EUR' },
  'Portugal': { locale: 'de-DE', timezone: 'Europe/Lisbon', currency: 'EUR' },
  'Ireland': { locale: 'en-GB', timezone: 'Europe/Dublin', currency: 'EUR' },
  'Sweden': { locale: 'en-GB', timezone: 'Europe/Stockholm', currency: 'SEK' },
  'Norway': { locale: 'en-GB', timezone: 'Europe/Oslo', currency: 'NOK' },
  'Denmark': { locale: 'en-GB', timezone: 'Europe/Copenhagen', currency: 'DKK' },
  'Finland': { locale: 'en-GB', timezone: 'Europe/Helsinki', currency: 'EUR' },
  'Poland': { locale: 'de-DE', timezone: 'Europe/Warsaw', currency: 'PLN' },
  'Czech Republic': { locale: 'de-DE', timezone: 'Europe/Prague', currency: 'EUR' },
  'Canada': { locale: 'en-US', timezone: 'America/Toronto', currency: 'USD' },
  'Australia': { locale: 'en-GB', timezone: 'Australia/Sydney', currency: 'USD' },
  'Japan': { locale: 'en-US', timezone: 'Asia/Tokyo', currency: 'JPY' },
};

// Fallback: derive from language code when country is not in the map
const LANGUAGE_DEFAULTS: Record<string, { locale: string; timezone: string; currency: string }> = {
  'de': { locale: 'de-DE', timezone: 'Europe/Berlin', currency: 'EUR' },
  'en': { locale: 'en-US', timezone: 'America/New_York', currency: 'USD' },
};

// AC-PW2-2: Persona step removed → 6 steps total (indices 0-5)
type StepIndex = 0 | 1 | 2 | 3 | 4 | 5;

export function RorosLaunchpad() {
  const navigate = useNavigate();
  const notifications = useNotificationStore();
  const setOnboardingComplete = useRorosStore((s) => s.setOnboardingComplete);
  const setStoreOperatingMode = useRorosStore((s) => s.setOperatingMode);
  const user = useAuthStore((s) => s.user);
  const orgs = useOrgStore((s) => s.orgs);
  const activeOrgId = useOrgStore((s) => s.activeOrgId);
  const activeOrg = orgs.find((o) => o.id === activeOrgId);

  // ─── FIX 1: Set locale from org language IMMEDIATELY ─────────────────────
  // This must run before the email verification gate so that UI is translated.
  const [localeApplied, setLocaleApplied] = React.useState(false);
  React.useEffect(() => {
    if (localeApplied) return;
    const orgLang = activeOrg?.default_language;
    if (orgLang) {
      // Convert short code (de, en) to full locale (de-DE, en-US)
      const fullLocale = orgLang.includes('-') ? orgLang : `${orgLang}-${orgLang.toUpperCase()}`;
      setLocale(fullLocale).catch(() => {});
      setLocaleApplied(true);
    }
  }, [activeOrg?.default_language, localeApplied]);

  // API hooks
  const { data: statusData, isLoading: statusLoading } = useRorosLaunchpadStatus();
  const submitStepMutation = useRorosLaunchpadSubmitStep();
  const completeMutation = useRorosLaunchpadComplete();

  // Current step index
  const [currentStep, setCurrentStep] = React.useState<StepIndex>(0);
  const [initialized, setInitialized] = React.useState(false);
  const [editingFromSummary, setEditingFromSummary] = React.useState(false);

  // ─── FIX 2: Derive Step 4 defaults from org's country/language ───────────
  // Resolution order:
  //   1. Country set → use COUNTRY_DEFAULTS (explicit user choice)
  //   2. No country → take language's locale + currency, but PREFER the
  //      browser's actual timezone (Intl.DateTimeFormat) over the language's
  //      static fallback. This avoids defaulting English speakers to
  //      America/New_York regardless of where they actually are.
  //   3. Final fallback → de-DE / Europe/Berlin / EUR for SSR / non-DOM envs.
  const orgDefaults = React.useMemo(() => {
    const country = activeOrg?.address_country;
    const lang = activeOrg?.default_language || 'de';
    if (country && COUNTRY_DEFAULTS[country]) return COUNTRY_DEFAULTS[country];

    // No country set — derive locale/currency from language, timezone from browser.
    const langDef = LANGUAGE_DEFAULTS[lang] ?? { locale: 'de-DE', timezone: 'Europe/Berlin', currency: 'EUR' };
    let browserTz: string | undefined;
    try {
      browserTz = Intl.DateTimeFormat().resolvedOptions().timeZone || undefined;
    } catch {
      // No DOM / Intl unavailable — keep language's static fallback
    }
    return { ...langDef, timezone: browserTz || langDef.timezone };
  }, [activeOrg?.address_country, activeOrg?.default_language]);

  // Step data (local state)
  const [operatingMode, setOperatingMode] = React.useState('founder');
  const [activeDomains, setActiveDomains] = React.useState<ActiveDomainEntry[]>([
    { domainId: 1, ledByAi: false },
    { domainId: 2, ledByAi: false },
    { domainId: 4, ledByAi: false },
    { domainId: 7, ledByAi: false },
  ]);
  // B08 fix: include website_url and research_website so they are submitted to the backend
  const [aiProvider, setAiProvider] = React.useState<{
    provider: string | null;
    api_key: string | null;
    website_url?: string | null;
    research_website?: boolean;
  }>({ provider: null, api_key: null });
  const [localeTz, setLocaleTz] = React.useState<{
    locale: string;
    timezone: string;
    currency: string;
    themePresetId?: string;
    themePresetName?: string;
  }>({
    locale: 'de-DE',
    timezone: 'Europe/Berlin',
    currency: 'EUR',
  });

  // Apply org-derived defaults once on mount (before status loads)
  const [defaultsApplied, setDefaultsApplied] = React.useState(false);
  React.useEffect(() => {
    if (!defaultsApplied && orgDefaults) {
      setLocaleTz((prev) => ({
        ...prev,
        locale: orgDefaults.locale,
        timezone: orgDefaults.timezone,
        currency: orgDefaults.currency,
      }));
      setDefaultsApplied(true);
    }
  }, [orgDefaults, defaultsApplied]);

  // B05: pre-fill Step 3 research URL from organizations.website_url captured at
  // registration. Only seeds if the field is still empty/unset — once
  // restoreStepData() populates it from saved launchpad_steps.data, or the user
  // edits it, this effect is a no-op thanks to the empty-check.
  const [websiteUrlSeeded, setWebsiteUrlSeeded] = React.useState(false);
  React.useEffect(() => {
    if (websiteUrlSeeded) return;
    const orgUrl = activeOrg?.website_url;
    if (!orgUrl) return;
    // Only seed if aiProvider.website_url is still empty — don't overwrite
    // restored step data or user edits.
    setAiProvider((prev) =>
      prev.website_url ? prev : { ...prev, website_url: orgUrl },
    );
    setWebsiteUrlSeeded(true);
  }, [activeOrg?.website_url, websiteUrlSeeded]);
  const [cadence, setCadence] = React.useState({
    schedules: [
      { type: 'daily', time: '09:00', day: 'weekdays', is_active: true },
      { type: 'weekly', time: '10:00', day: 'Monday', is_active: true },
      { type: 'monthly', time: '14:00', day: '1st Monday', is_active: true },
      { type: 'quarterly', time: '14:00', day: 'Last week', is_active: true },
      { type: 'trimester', time: '14:00', day: 'Last week', is_active: false },
      { type: 'annual', time: '10:00', day: 'December', is_active: true },
    ],
    planning_cycle: 'quarterly' as 'quarterly' | 'trimester',
  });

  // Update locale store when locale selection changes so downstream components
  // (e.g. TimeInput in StepCadence) reflect the correct time/date format
  const updateLocaleSettings = useLocaleStore((s) => s.updateSettings);
  React.useEffect(() => {
    const LOCALE_FORMAT_MAP: Record<string, Partial<LocaleSettings>> = {
      'en-US': { dateFormat: 'MM/DD/YYYY', timeFormat: 'hh:mm A', thousandsSeparator: ',', decimalSeparator: '.', currencySymbol: '$', currencyFormat: 'symbol_first' },
      'en-GB': { dateFormat: 'DD/MM/YYYY', timeFormat: 'HH:mm', thousandsSeparator: ',', decimalSeparator: '.', currencySymbol: '£', currencyFormat: 'symbol_first' },
    };
    // Default (de-DE and others): 24h format
    const defaults: Partial<LocaleSettings> = { dateFormat: 'DD.MM.YYYY', timeFormat: 'HH:mm', thousandsSeparator: '.', decimalSeparator: ',', currencySymbol: '€', currencyFormat: 'value_first' };
    const settings = LOCALE_FORMAT_MAP[localeTz.locale] || defaults;
    // Override currency symbol from the selected currency
    const currencySymbols: Record<string, string> = { EUR: '€', USD: '$', GBP: '£', CHF: 'Fr.', SEK: 'kr', NOK: 'kr', DKK: 'kr', PLN: 'zł', JPY: '¥' };
    if (localeTz.currency && currencySymbols[localeTz.currency]) {
      settings.currencySymbol = currencySymbols[localeTz.currency];
    }
    updateLocaleSettings(settings);
  }, [localeTz.locale, localeTz.currency, updateLocaleSettings]);

  // Refine locale from saved step data (if user previously saved a locale_tz step)
  React.useEffect(() => {
    if (statusData) {
      const localeStep = statusData.steps?.find((s) => s.step === 'locale_tz');
      const savedLanguage = localeStep?.data?.language as string | undefined;
      if (savedLanguage) {
        setLocale(savedLanguage).catch(() => {});
      }
    }
  }, [statusData]);

  // Resume: jump to first incomplete step on status load
  React.useEffect(() => {
    if (statusData && !initialized) {
      setInitialized(true);

      // If already complete, redirect (backend DTO uses camelCase isComplete)
      if (statusData.isComplete || statusData.is_complete) {
        setOnboardingComplete(true);
        navigate('/roros', { replace: true });
        return;
      }

      // Restore saved step data and find first incomplete step
      let firstIncomplete = 0;
      for (let i = 0; i < LAUNCHPAD_STEPS.length; i++) {
        const stepKey = LAUNCHPAD_STEPS[i];
        const saved = statusData.steps.find((s) => s.step === stepKey);
        if (saved?.completed || saved?.skipped) {
          // Restore saved data to local state
          if (saved.data && !saved.skipped) {
            restoreStepData(stepKey, saved.data);
          }
          firstIncomplete = i + 1;
        } else {
          break;
        }
      }

      // Clamp to valid range
      const targetStep = Math.min(firstIncomplete, LAUNCHPAD_STEPS.length - 1) as StepIndex;
      setCurrentStep(targetStep);
    }
  }, [statusData, initialized, navigate, setOnboardingComplete]);

  const restoreStepData = (step: LaunchpadStepKey, data: Record<string, unknown>) => {
    switch (step) {
      case 'operating_mode':
        if (data.value) setOperatingMode(data.value as string);
        else if (data.operating_mode) setOperatingMode(data.operating_mode as string);
        break;
      case 'active_domains':
        setActiveDomains(normalizeActiveDomains(data));
        break;
      case 'ai_provider':
        if (data.skipped) break;
        setAiProvider({
          provider: (data.provider as string | null) || null,
          // B08 fix: key is stripped server-side to has_key=true; don't try to restore it
          api_key: null,
          website_url: (data.website_url as string | null) || null,
          research_website: (data.research_website as boolean) || false,
        });
        break;
      case 'locale_tz':
        if (data.language || data.timezone) {
          setLocaleTz({
            locale: (data.language as string) || 'de-DE',
            timezone: (data.timezone as string) || 'Europe/Berlin',
            currency: (data.currency as string) || 'EUR',
            themePresetId: (data.theme_preset_id as string) || undefined,
          });
        }
        break;
      case 'cadence':
        if (data.planning_cycle) {
          const activeCadenceTypes = (data.cadence_types as string[]) ?? [];
          setCadence({
            schedules: cadence.schedules.map((s) => ({
              ...s,
              is_active: activeCadenceTypes.includes(s.type),
            })),
            planning_cycle: (data.planning_cycle as 'quarterly' | 'trimester') || 'quarterly',
          });
        }
        break;
    }
  };

  const getStepData = (step: LaunchpadStepKey): Record<string, unknown> => {
    switch (step) {
      case 'operating_mode':
        return { value: operatingMode };
      case 'active_domains':
        return { active_domains: activeDomains };
      case 'ai_provider':
        if (!aiProvider.provider) return { skipped: true };
        // B08 fix: send api_key (not model), and include website_url + research_website
        // so the backend can create the AI config and enqueue the research job correctly.
        return {
          provider: aiProvider.provider,
          ...(aiProvider.api_key ? { api_key: aiProvider.api_key } : {}),
          ...(aiProvider.website_url != null ? { website_url: aiProvider.website_url } : {}),
          ...(aiProvider.research_website !== undefined ? { research_website: aiProvider.research_website } : {}),
        };
      case 'locale_tz':
        return {
          timezone: localeTz.timezone,
          language: localeTz.locale,
          currency: localeTz.currency,
          ...(localeTz.themePresetId ? { theme_preset_id: localeTz.themePresetId } : {}),
        };
      case 'cadence':
        return {
          planning_cycle: cadence.planning_cycle,
          cadence_types: cadence.schedules
            .filter((s) => s.is_active)
            .map((s) => s.type),
        };
      case 'summary':
        return {};
      default:
        return {};
    }
  };

  // Navigate to next step — save current step first
  const handleNext = async () => {
    const stepKey = LAUNCHPAD_STEPS[currentStep];
    try {
      await submitStepMutation.mutateAsync({
        step: stepKey,
        data: getStepData(stepKey),
      });

      // FIX 6: Apply locale immediately when saving locale_tz step
      if (stepKey === 'locale_tz') {
        try {
          await setLocale(localeTz.locale);
        } catch {
          // Non-blocking — locale will apply on next load
        }
      }

      // FIX 3: If editing from summary, jump back to summary
      if (editingFromSummary) {
        setEditingFromSummary(false);
        setCurrentStep(5 as StepIndex);
      } else if (currentStep < LAUNCHPAD_STEPS.length - 1) {
        setCurrentStep((currentStep + 1) as StepIndex);
      }
    } catch (err) {
      notifications.error(
        err instanceof Error ? err.message : 'Failed to save step'
      );
    }
  };

  // Navigate back
  const handleBack = () => {
    if (currentStep > 0) {
      // FIX 3c: If going back from summary, track it so Next returns to summary
      if (LAUNCHPAD_STEPS[currentStep] === 'summary') {
        setEditingFromSummary(true);
      }
      setCurrentStep((currentStep - 1) as StepIndex);
    }
  };

  // Skip current step — use defaults
  const handleSkip = async () => {
    const stepKey = LAUNCHPAD_STEPS[currentStep];
    const defaults = STEP_DEFAULTS[stepKey];
    try {
      await submitStepMutation.mutateAsync({
        step: stepKey,
        data: defaults as Record<string, unknown>,
      });
      if (currentStep < LAUNCHPAD_STEPS.length - 1) {
        setCurrentStep((currentStep + 1) as StepIndex);
      }
    } catch (err) {
      notifications.error(
        err instanceof Error ? err.message : 'Failed to skip step'
      );
    }
  };

  // Jump to a specific step (from summary "Edit" links)
  const handleJumpToStep = (stepKey: LaunchpadStepKey) => {
    const index = LAUNCHPAD_STEPS.indexOf(stepKey);
    if (index >= 0) {
      // FIX 3: Track that we came from summary so Next returns us there
      if (LAUNCHPAD_STEPS[currentStep] === 'summary') {
        setEditingFromSummary(true);
      }
      setCurrentStep(index as StepIndex);
    }
  };

  // Complete onboarding
  const handleComplete = async () => {
    try {
      await completeMutation.mutateAsync();
      setOnboardingComplete(true);
      setStoreOperatingMode(operatingMode as RorosOperatingMode);

      // FIX 4: Re-fetch and apply org theme after onboarding
      // The seed generation wrote the theme preset to organization_themes,
      // but useOrgTheme won't re-fetch because activeOrgId hasn't changed.
      if (activeOrgId) {
        try {
          const themeRes = await apiFetch(`/api/orgs/${activeOrgId}/resolved-theme`);
          if (themeRes.ok) {
            const json = await themeRes.json();
            if (json?.data) {
              applyTheme(json.data as ResolvedTheme);
            }
          }
        } catch {
          // Non-fatal — theme is cosmetic
        }
      }

      notifications.success(t('Onboarding complete! Welcome to Rocket Routine OS.'));
      // J1-C: Post-onboarding lands on the Outcomes page (PRD v3.4 GAP 15).
      navigate('/roros/artifacts/outcomes', { replace: true });
    } catch (err) {
      notifications.error(
        err instanceof Error ? err.message : t('Failed to complete setup. Please try again.')
      );
    }
  };

  // Sign out
  const handleSignOut = async () => {
    await useAuthStore.getState().logout();
    navigate('/', { replace: true });
  };

  // ─── Email verification gate ───────────────────────────────────────────
  // Block onboarding until the user has verified their email address.
  const isEmailVerified = user?.isEmailVerified ?? false;
  const [resendingVerification, setResendingVerification] = React.useState(false);
  const [resendMessage, setResendMessage] = React.useState<string | null>(null);

  if (!isEmailVerified) {
    const handleResendVerification = async () => {
      setResendingVerification(true);
      setResendMessage(null);
      try {
        const { apiFetch } = await import('@/lib/api');
        const res = await apiFetch('/api/auth/resend-verification', { method: 'POST' });
        setResendMessage(res.ok
          ? t('Verification email sent! Check your inbox.')
          : t('Failed to resend. Please try again.'));
      } catch {
        setResendMessage(t('Failed to resend. Please try again.'));
      } finally {
        setResendingVerification(false);
      }
    };

    const handleRefreshVerification = async () => {
      try { await useAuthStore.getState().fetchMe(); } catch { /* ignore */ }
    };

    return (
      <div className="min-h-screen bg-gradient-to-br from-primary-50 via-white to-secondary-50 flex items-center justify-center p-4">
        <div className="w-full max-w-lg">
          <div className="bg-background rounded-2xl shadow-xl overflow-hidden border">
            <div className="p-6 md:p-8">
              <div className="flex items-center justify-between mb-2">
                <h2 className="text-xl font-semibold">{t('Verify your email')}</h2>
                <Button variant="ghost" size="sm" className="whitespace-nowrap inline-flex items-center gap-2" onClick={async () => { await useAuthStore.getState().logout(); navigate('/', { replace: true }); }}>
                  <LogOut className="w-4 h-4" />
                  <span>{t('Sign out')}</span>
                </Button>
              </div>
              <div className="mt-6 flex flex-col items-center text-center gap-4">
                <div className="w-16 h-16 rounded-full bg-primary-100 flex items-center justify-center">
                  <Mail className="w-8 h-8 text-primary-600" />
                </div>
                <p className="text-sm text-muted-foreground">
                  {t('We sent a verification email to')} <strong>{user?.email}</strong>. {t('Please check your inbox and click the verification link to continue.')}
                </p>
                {resendMessage && (
                  <Alert variant={resendMessage.includes(t('sent') || 'sent') ? 'success' : 'error'}>{resendMessage}</Alert>
                )}
                <div className="flex gap-3">
                  <Button variant="outline" onClick={handleResendVerification} loading={resendingVerification}>{t('Resend email')}</Button>
                  <Button variant="primary" onClick={handleRefreshVerification}>{t("I've verified — continue")}</Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Loading state
  if (statusLoading || !initialized) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-primary-50 via-white to-secondary-50 flex items-center justify-center">
        <div className="text-center">
          <Spinner size="lg" />
          <p className="mt-4 text-muted-foreground">Loading onboarding...</p>
        </div>
      </div>
    );
  }

  const isLastStep = currentStep === LAUNCHPAD_STEPS.length - 1;
  const isSummaryStep = LAUNCHPAD_STEPS[currentStep] === 'summary';
  const isSubmitting = submitStepMutation.isPending || completeMutation.isPending;

  // FIX 4: Build steps using backend completion status so checkmarks persist
  const steps: Step[] = LAUNCHPAD_STEPS.map((key, index) => {
    const saved = statusData?.steps?.find((s) => s.step === key);
    const isCompleted = !!(saved?.completed || saved?.skipped);
    let status: 'completed' | 'current' | 'pending';
    if (index === currentStep) {
      status = 'current';
    } else if (isCompleted) {
      status = 'completed';
    } else {
      status = 'pending';
    }
    return {
      id: `step-${index}`,
      label: t(STEP_LABELS[key]),
      status,
    };
  });

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 via-white to-secondary-50">
      {/* Header */}
      <div className="border-b bg-white/80 backdrop-blur-sm sticky top-0 z-10">
        <div className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/logo_mark.png" alt="Rocket Routine" className="w-7 h-7" />
            <span className="font-semibold text-lg text-gray-900" data-no-translate>Rocket Routine OS</span>
          </div>
          <div className="flex items-center gap-4">
            {/* FIX 5: User identity display */}
            {(activeOrg || user) && (
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                {activeOrg && (
                  <span className="hidden sm:inline font-medium">{activeOrg.name}</span>
                )}
                {activeOrg && user && (
                  <span className="hidden sm:inline text-gray-300">·</span>
                )}
                {user && (
                  <div className="flex items-center gap-1.5">
                    <Avatar name={user.fullName || user.email} size="xs" />
                    <span className="hidden sm:inline">{user.fullName || user.email}</span>
                  </div>
                )}
              </div>
            )}
            <Button
              variant="ghost"
              size="sm"
              onClick={handleSignOut}
              leftIcon={<LogOut className="w-4 h-4" />}
            >{t("Sign out")}</Button>
          </div>
        </div>
      </div>

      {/* Main content */}
      <div className="max-w-5xl mx-auto px-6 py-8">
        {/* Step indicator */}
        <div className="mb-8">
          <Steps
            steps={steps}
            size="sm"
            onStepClick={(step, index) => {
              // Allow clicking on completed steps (even those after current)
              if (step.status === 'completed') {
                // FIX 3b: Track that we came from summary so Next returns us there
                if (LAUNCHPAD_STEPS[currentStep] === 'summary') {
                  setEditingFromSummary(true);
                }
                setCurrentStep(index as StepIndex);
              }
            }}
          />
        </div>

        {/* Step content */}
        <div>
          <div className="bg-background rounded-2xl shadow-lg border p-6 md:p-8 min-h-[400px]">
            {currentStep === 0 && (
              <StepOperatingMode value={operatingMode} onChange={setOperatingMode} autoFocus />
            )}
            {currentStep === 1 && (
              <StepActiveDomains value={activeDomains} onChange={setActiveDomains} autoFocus />
            )}
            {currentStep === 2 && (
              <StepAIProvider value={aiProvider} onChange={setAiProvider} autoFocus />
            )}
            {currentStep === 3 && (
              <StepLocaleTz value={localeTz} onChange={setLocaleTz} autoFocus />
            )}
            {/* AC-PW2-2: persona_setup (step 4) removed — now step 4 = Cadence */}
            {currentStep === 4 && (
              <StepCadence
                value={cadence}
                onChange={setCadence}
                operatingMode={operatingMode}
                locale={localeTz.locale}
              />
            )}
            {currentStep === 5 && (
              <StepSummary
                operatingMode={operatingMode}
                activeDomains={activeDomains}
                aiProvider={aiProvider}
                localeTz={localeTz}
                cadence={cadence}
                onEdit={handleJumpToStep}
              />
            )}
          </div>

          {/* Navigation buttons */}
          <div className="flex items-center justify-between mt-6">
            <div>
              {currentStep > 0 && (
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleBack}
                  disabled={isSubmitting}
                  leftIcon={<ChevronLeft className="w-4 h-4" />}
                >{t("Back")}</Button>
              )}
            </div>

            <div className="flex items-center gap-3">
              {/* Skip button (not on summary step) */}
              {!isSummaryStep && (
                <Button
                  type="button"
                  variant="ghost"
                  onClick={handleSkip}
                  disabled={isSubmitting}
                  leftIcon={<SkipForward className="w-4 h-4" />}
                >{t("Skip (use defaults)")}</Button>
              )}

              {/* Next / Complete button */}
              {isSummaryStep ? (
                <Button
                  type="button"
                  onClick={handleComplete}
                  variant="primary"
                  loading={completeMutation.isPending}
                  disabled={isSubmitting}
                  leftIcon={<Check className="w-4 h-4" />}
                  autoFocus
                >{t("Complete Setup")}</Button>
              ) : (
                <Button
                  type="button"
                  onClick={handleNext}
                  variant="primary"
                  loading={submitStepMutation.isPending}
                  disabled={isSubmitting}
                  rightIcon={<ChevronRight className="w-4 h-4" />}
                >{t("Next")}</Button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
